import { useMemo, useState, useEffect, useCallback } from 'react';
import { Card, Row, Col, Button, Form, Badge, Alert } from 'react-bootstrap';
import { InteractiveQuestion } from '@components';
import { useAnswerSubmission } from '@hooks';
import { QUESTION_REASON } from '@config/constants';
import PropTypes from 'prop-types';

function shuffleArray(items) {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

export function QuestionOnlySession({ course, progress, userId }) {
  const courseId = course?.id;
  const { submitAnswer, submitting } = useAnswerSubmission(userId, courseId);
  const [selectedSectionIds, setSelectedSectionIds] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [questionQueue, setQuestionQueue] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [shuffleSeed, setShuffleSeed] = useState(0);
  const [sessionScores, setSessionScores] = useState({});

  const questionPoolScope = course?.metadata?.questionPoolScope || 'unlocked';

  const baseSections = useMemo(() => {
    if (!course?.sections?.length) return [];
    if (questionPoolScope === 'all') {
      return course.sections;
    }
    const unlockedSectionIds = progress?.unlockedSections || [];
    const unlocked = course.sections.filter((section) => unlockedSectionIds.includes(section.id));
    return unlocked.length > 0 ? unlocked : [course.sections[0]];
  }, [course, questionPoolScope, progress?.unlockedSections]);

  useEffect(() => {
    setSelectedSectionIds(baseSections.map((section) => section.id));
  }, [baseSections]);

  const availableTags = useMemo(() => {
    const tagSet = new Set();
    baseSections.forEach((section) => {
      (section.questions || []).forEach((question) => {
        (question.tags || []).forEach((tag) => tagSet.add(tag));
      });
    });
    return Array.from(tagSet).sort((a, b) => a.localeCompare(b));
  }, [baseSections]);

  useEffect(() => {
    setSelectedTags((prev) => prev.filter((tag) => availableTags.includes(tag)));
  }, [availableTags]);

  const filteredQuestions = useMemo(() => {
    const selectedSections = baseSections.filter((section) => selectedSectionIds.includes(section.id));
    const questions = selectedSections.flatMap((section) =>
      (section.questions || []).map((question) => ({
        ...question,
        sectionId: question.sectionId || section.id,
      }))
    );

    if (selectedTags.length === 0) {
      return questions;
    }

    return questions.filter((question) =>
      (question.tags || []).some((tag) => selectedTags.includes(tag))
    );
  }, [baseSections, selectedSectionIds, selectedTags]);

  useEffect(() => {
    setQuestionQueue(shuffleArray(filteredQuestions));
    setCurrentIndex(0);
  }, [filteredQuestions, shuffleSeed]);

  const currentQuestion = questionQueue[currentIndex] || null;
  const hasQuestions = questionQueue.length > 0;
  const isComplete = hasQuestions && currentIndex >= questionQueue.length;

  const handleNextQuestion = useCallback(() => {
    setCurrentIndex((prev) => Math.min(prev + 1, questionQueue.length));
  }, [questionQueue.length]);

  const handleReshuffle = () => {
    setShuffleSeed((prev) => prev + 1);
  };

  const recordSessionScore = (sectionId, isCorrect) => {
    if (!sectionId) return;
    setSessionScores((prev) => {
      const current = prev[sectionId] || { attempted: 0, correct: 0 };
      const attempted = current.attempted + 1;
      const correct = current.correct + (isCorrect ? 1 : 0);
      return {
        ...prev,
        [sectionId]: { attempted, correct },
      };
    });
  };

  const sessionScoreForSection = (sectionId) => {
    const stats = sessionScores[sectionId];
    if (!stats || stats.attempted === 0) return null;
    return Math.round((stats.correct / stats.attempted) * 100);
  };

  const sessionScoreForSelected = useMemo(() => {
    if (selectedSectionIds.length === 0) return null;
    let attempted = 0;
    let correct = 0;
    selectedSectionIds.forEach((sectionId) => {
      const stats = sessionScores[sectionId];
      if (stats?.attempted) {
        attempted += stats.attempted;
        correct += stats.correct;
      }
    });
    if (attempted === 0) return null;
    return {
      attempted,
      correct,
      percent: Math.round((correct / attempted) * 100),
    };
  }, [selectedSectionIds, sessionScores]);

  const handleToggleSection = (sectionId) => {
    setSelectedSectionIds((prev) => {
      if (prev.includes(sectionId)) {
        return prev.filter((id) => id !== sectionId);
      }
      return [...prev, sectionId];
    });
  };

  const handleToggleTag = (tag) => {
    setSelectedTags((prev) => {
      if (prev.includes(tag)) {
        return prev.filter((t) => t !== tag);
      }
      return [...prev, tag];
    });
  };

  const handleSelectAllSections = () => {
    setSelectedSectionIds(baseSections.map((section) => section.id));
  };

  const handleClearSections = () => {
    setSelectedSectionIds([]);
  };

  const sectionScore = (sectionId) => {
    const score = progress?.sectionProgress?.[sectionId]?.score || 0;
    return Math.round(score);
  };

  if (!course || !progress) {
    return (
      <Card className="text-center p-5">
        <p className="text-muted mb-0">Loading questions...</p>
      </Card>
    );
  }

  return (
    <Row className="g-3">
      <Col lg={4}>
        <Card className="mb-3">
          <Card.Body>
            <div className="d-flex align-items-center justify-content-between mb-2">
              <h6 className="mb-0">Question Pool</h6>
              <Badge bg={questionPoolScope === 'all' ? 'info' : 'success'}>
                {questionPoolScope === 'all' ? 'All Sections' : 'Unlocked Sections'}
              </Badge>
            </div>
            <p className="text-muted small mb-3">
              Choose which sections to practice and filter by tags. Questions are shown in a random order.
            </p>
            <div className="d-flex gap-2 mb-3">
              <Button variant="outline-secondary" size="sm" onClick={handleSelectAllSections}>
                Select all
              </Button>
              <Button variant="outline-secondary" size="sm" onClick={handleClearSections}>
                Clear
              </Button>
            </div>
            <div className="question-only-section-list">
              {baseSections.map((section) => {
                const isUnlocked = progress.unlockedSections?.includes(section.id);
                return (
                  <Form.Check
                    key={section.id}
                    type="checkbox"
                    id={`section-${section.id}`}
                    className="mb-2"
                    checked={selectedSectionIds.includes(section.id)}
                    onChange={() => handleToggleSection(section.id)}
                    label={
                      <span>
                        {section.title}{' '}
                        <Badge bg="light" text="dark" className="ms-1">
                          {sectionScore(section.id)}%
                        </Badge>
                        {!isUnlocked && questionPoolScope === 'all' && (
                          <Badge bg="secondary" className="ms-2">Locked</Badge>
                        )}
                      </span>
                    }
                  />
                );
              })}
              {baseSections.length === 0 && (
                <Alert variant="warning" className="mt-2">
                  No sections available for this question pool.
                </Alert>
              )}
            </div>
          </Card.Body>
        </Card>

        <Card>
          <Card.Body>
            <h6 className="mb-2">Filter by Tags</h6>
            {availableTags.length === 0 ? (
              <p className="text-muted small mb-0">No tags found in the selected sections.</p>
            ) : (
              <div className="d-flex flex-wrap gap-2">
                {availableTags.map((tag) => (
                  <Button
                    key={tag}
                    size="sm"
                    variant={selectedTags.includes(tag) ? 'primary' : 'outline-primary'}
                    onClick={() => handleToggleTag(tag)}
                  >
                    {tag}
                  </Button>
                ))}
              </div>
            )}
            {selectedTags.length === 0 && availableTags.length > 0 && (
              <small className="text-muted d-block mt-2">All tags selected</small>
            )}
          </Card.Body>
        </Card>

        <Card className="mt-3">
          <Card.Body>
            <h6 className="mb-2">Session Score</h6>
            <small className="text-muted d-block mb-2">
              Practice-only scoring for the selected sections.
            </small>
            {baseSections.length === 0 ? (
              <p className="text-muted small mb-0">No sections available.</p>
            ) : (
              <div className="d-flex flex-column gap-2">
                {baseSections.map((section) => {
                  const stats = sessionScores[section.id];
                  const percent = sessionScoreForSection(section.id);
                  return (
                    <div key={`score-${section.id}`} className="d-flex justify-content-between align-items-center">
                      <span className="small">{section.title}</span>
                      <div className="d-flex align-items-center gap-2">
                        {stats?.attempted ? (
                          <Badge bg="info">{percent}%</Badge>
                        ) : (
                          <Badge bg="light" text="dark">—</Badge>
                        )}
                        <small className="text-muted">
                          {stats?.attempted ? `${stats.correct}/${stats.attempted}` : '0/0'}
                        </small>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card.Body>
        </Card>
      </Col>

      <Col lg={8}>
        <Card className="mb-3">
          <Card.Body className="d-flex flex-wrap justify-content-between align-items-center gap-2">
            <div>
              <h5 className="mb-1">Question Practice</h5>
              <small className="text-muted">
                {hasQuestions ? `${Math.min(currentIndex + 1, questionQueue.length)} / ${questionQueue.length}` : '0 / 0'}
              </small>
            </div>
            <div className="d-flex align-items-center gap-2">
              <small className="text-muted">Session score</small>
              {sessionScoreForSelected ? (
                <Badge bg="info">
                  {sessionScoreForSelected.percent}% ({sessionScoreForSelected.correct}/{sessionScoreForSelected.attempted})
                </Badge>
              ) : (
                <Badge bg="light" text="dark">—</Badge>
              )}
            </div>
            <div className="d-flex gap-2">
              <Button variant="outline-primary" onClick={handleReshuffle} disabled={!hasQuestions}>
                Reshuffle
              </Button>
              <Button variant="outline-secondary" onClick={() => setCurrentIndex(0)} disabled={!hasQuestions}>
                Restart
              </Button>
            </div>
          </Card.Body>
        </Card>

        {!hasQuestions && (
          <Card className="text-center p-5">
            <p className="text-muted mb-0">No questions match the selected filters.</p>
          </Card>
        )}

        {isComplete && hasQuestions && (
          <Card className="text-center p-5">
            <h5 className="mb-2">You reached the end of the queue.</h5>
            <p className="text-muted">Reshuffle to keep practicing.</p>
            <Button variant="primary" onClick={handleReshuffle}>
              Shuffle Again
            </Button>
          </Card>
        )}

        {currentQuestion && !isComplete && (
          <InteractiveQuestion
            question={currentQuestion}
            userId={userId}
            courseId={courseId}
            course={course}
            reason={QUESTION_REASON.PREVIEW}
            currentSectionId={currentQuestion.sectionId}
            questionSectionId={currentQuestion.sectionId}
            sectionMastery={sectionScore(currentQuestion.sectionId)}
            showExplanation
            disabled={submitting}
            isRoundComplete={currentIndex === questionQueue.length - 1}
            onSubmit={(data) => {
              if (!userId || !courseId) return;
              recordSessionScore(currentQuestion.sectionId, data?.isCorrect);
              return submitAnswer(currentQuestion.id, data, currentQuestion.tags || []);
            }}
            onNext={handleNextQuestion}
            onContinue={handleNextQuestion}
          />
        )}
      </Col>
    </Row>
  );
}

QuestionOnlySession.propTypes = {
  course: PropTypes.object,
  progress: PropTypes.object,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
